import { MARKETPLACE_META, type MarketplaceCode, type SafetyReviewField } from "./types";
import {
  inspectSafetySchema, safetyChoiceValue, safetyEnumOptions, safetyOptionPresentation, safetyRecord,
  type SafetyEnumOption, type SafetySchema,
} from "./safetySchema";

const hazardStatements = [
  { code: "H317", text: "Kann allergische Hautreaktionen verursachen." },
  { code: "H412", text: "Schädlich für Wasserorganismen, mit langfristiger Wirkung." },
];
const precautionaryStatements = [
  { code: "P101", text: "Ist ärztlicher Rat erforderlich, Verpackung oder Kennzeichnungsetikett bereithalten." },
  { code: "P102", text: "Darf nicht in die Hände von Kindern gelangen." },
  { code: "P302+P352", text: "BEI BERÜHRUNG MIT DER HAUT: Mit viel Wasser waschen." },
  { code: "P333+P313", text: "Bei Hautreizung oder -ausschlag: Ärztlichen Rat einholen/ärztliche Hilfe hinzuziehen." },
  { code: "P501", text: "Inhalt / Behälter entsprechend den örtlichen Vorschriften der Entsorgung zuführen." },
];

/** Product label facts from section 2; ingredient classifications are intentionally excluded. */
export const FRAGRANCE_SAFETY_PRESET = {
  id: "elina-winterapfel-sds-2023-03-29-v3",
  label: "Duftöl – ELINA Winterapfel",
  productName: "ELINA Winterapfel",
  documentDate: "29.03.2023",
  sourceUrl: "https://www.jean-products.de/media/site/7e35ab0ba6-1721132162/69014-duftoel-10ml-winterapfel-in-glasflasche-sdb.pdf",
  documentVersion: "3",
  pictograms: ["GHS07"],
  signalWord: "Achtung",
  hazardStatements,
  precautionaryStatements,
  safetyWarning: ["Achtung", ...hazardStatements.map(item => `${item.code} ${item.text}`), ...precautionaryStatements.map(item => `${item.code} ${item.text}`)].join("\n"),
} as const;

const FACTS = ["GHS07", "warning", ...hazardStatements.map(item => item.code), ...precautionaryStatements.map(item => item.code)];
const TARGET_CODES = new Set(FACTS);
const BLOCKED_NAMES = /(?:manufacturer|responsible_person|gpsr|ufi|supplier|transport|hazmat|dangerous_goods|dg_hz|contact|email)/i;
type Context = "signal" | "warning" | "codes";
interface Mapped { value: unknown; facts: string[] }
interface ChoiceSchema { schema: SafetySchema; object?: SafetySchema }
export interface FragranceSafetyPresetResult {
  attributes: Record<string, unknown>;
  appliedFields: string[];
  notices: string[];
}

function contextFor(name: string, inherited: Context = "codes"): Context {
  if (/signal_?word/i.test(name)) return "signal";
  if (/^safety_warnings?$/i.test(name)) return "warning";
  return inherited;
}
function equal(a: unknown, b: unknown): boolean { return JSON.stringify(a) === JSON.stringify(b); }
function signalMeaning(option: SafetyEnumOption): string | undefined {
  for (const value of [option.value, option.label]) {
    if (typeof value !== "string") continue;
    if (/^(warning|achtung)$/i.test(value.trim())) return "warning";
    if (/^(danger|gefahr)$/i.test(value.trim())) return "danger";
  }
  return undefined;
}
function meaning(option: SafetyEnumOption, context: Context): string | undefined {
  return context === "signal" ? signalMeaning(option) : safetyOptionPresentation(option.value, option.label).code;
}
function metadataSeed(schema: SafetySchema, root: SafetySchema, marketplaceId?: string): Record<string, unknown> {
  const result: Record<string, unknown> = {};
  if (!safetyRecord(schema.properties)) return result;
  for (const [name, raw] of Object.entries(schema.properties)) {
    if (!safetyRecord(raw)) continue;
    const inspected = inspectSafetySchema(raw, root);
    if (inspected.reason) continue;
    const child = inspected.schema;
    // Only schema-defined metadata is seeded; compliance defaults are not product evidence.
    if (/^(marketplace_id|language_tag)$/.test(name)) {
      if ("const" in child) result[name] = structuredClone(child.const);
      else if ("default" in child) result[name] = structuredClone(child.default);
      else if (name === "marketplace_id" && marketplaceId && safetyEnumOptions(child).some(option => option.value === marketplaceId)) result[name] = marketplaceId;
    }
  }
  return result;
}

/** Applies only choices actually offered by the live field schema, without mutating its inputs. */
export function applyFragranceSafetyPreset({ fields, attributes, marketplace, allowGermanText = marketplace.toUpperCase() === "DE" }: {
  fields: SafetyReviewField[];
  attributes: Record<string, unknown>;
  marketplace: string;
  allowGermanText?: boolean;
}): FragranceSafetyPresetResult {
  const notices = new Set<string>();
  const result = structuredClone(attributes);
  const appliedFields: string[] = [];
  const covered = new Set<string>();
  const marketplaceId = MARKETPLACE_META[marketplace.toUpperCase() as MarketplaceCode]?.marketplaceId;
  const notice = (path: string, text: string) => notices.add(`${path}: ${text}`);
  const unchanged = (value: unknown): Mapped => ({ value, facts: [] });

  function requiredSatisfied(value: Record<string, unknown>, schema: SafetySchema, path: string): boolean {
    const missing = (Array.isArray(schema.required) ? schema.required : []).filter(key => typeof key === "string" && value[key] === undefined);
    if (!missing.length) return true;
    notice(path, `Nicht übernommen: Für Pflichtangaben (${missing.join(", ")}) fehlen belegte Werte.`);
    return false;
  }
  function arrayFits(value: unknown[], schema: SafetySchema, path: string): boolean {
    for (const [key, invalid] of [["minItems", typeof schema.minItems === "number" && value.length < schema.minItems], ["maxItems", typeof schema.maxItems === "number" && value.length > schema.maxItems]] as const) {
      if (invalid) { notice(path, `Nicht übernommen: Die vollständige Auswahl verletzt ${key} im Amazon-Schema.`); return false; }
    }
    return true;
  }
  function choiceSchema(raw: SafetySchema, root: SafetySchema): ChoiceSchema | undefined {
    const item = inspectSafetySchema(raw, root);
    if (item.reason || "const" in item.schema) return undefined;
    if (item.type !== "object") return safetyEnumOptions(item.schema).length ? { schema: item.schema } : undefined;
    if (!safetyRecord(item.schema.properties) || !safetyRecord(item.schema.properties.value)) return undefined;
    const value = inspectSafetySchema(item.schema.properties.value, root);
    if (value.reason || "const" in value.schema || !safetyEnumOptions(value.schema).length) return undefined;
    return { schema: value.schema, object: item.schema };
  }
  function applyChoices(current: unknown, selection: ChoiceSchema, context: Context, path: string, root: SafetySchema, arraySchema?: SafetySchema): Mapped {
    const options = safetyEnumOptions(selection.schema).map(option => ({ ...option, fact: meaning(option, context) }));
    const desired = options.filter(option => option.fact && TARGET_CODES.has(option.fact));
    if (!desired.length) return unchanged(current);
    const duplicate = desired.find((option, index) => desired.findIndex(other => other.fact === option.fact) !== index);
    if (duplicate) { notice(path, `Mehrere Amazon-Werte bedeuten ${duplicate.fact}; die Zuordnung ist nicht eindeutig.`); return unchanged(current); }
    const known = (value: unknown) => options.find(option => Object.is(option.value, safetyChoiceValue(value)))?.fact;
    if (!arraySchema) {
      if (desired.length !== 1) { notice(path, "Nicht übernommen: Dieses Einzelfeld kann nicht alle belegten Angaben aufnehmen."); return unchanged(current); }
      if (current !== null && typeof current === "object") { notice(path, "Die vorhandene Struktur wurde erhalten."); return unchanged(current); }
      if (current !== undefined && current !== null && current !== "" && !known(current)) { notice(path, "Der vorhandene unbekannte Wert wurde erhalten."); return unchanged(current); }
      return { value: desired[0].value, facts: [desired[0].fact!] };
    }
    if (current != null && !Array.isArray(current)) { notice(path, "Die vorhandene Struktur ist keine Liste und wurde erhalten."); return unchanged(current); }
    const old = Array.isArray(current) ? current : [];
    const unknown = old.filter(item => !known(item));
    const reusable = old.filter(item => known(item) && !desired.some(option => Object.is(option.value, safetyChoiceValue(item))));
    const next: unknown[] = [];
    for (const option of desired) {
      const matching = old.find(item => Object.is(option.value, safetyChoiceValue(item)));
      if (selection.object) {
        const base = matching ?? reusable.shift();
        const object = { ...metadataSeed(selection.object, root, marketplaceId), ...(safetyRecord(base) ? base : {}), value: option.value };
        if (!requiredSatisfied(object, selection.object, path)) return unchanged(current);
        next.push(object);
      } else next.push(option.value);
    }
    next.push(...unknown);
    const lostMetadata = old.some(item => safetyRecord(item) && known(item) && !next.some(candidate =>
      safetyRecord(candidate) && Object.entries(item).every(([key, value]) => key === "value" || equal(candidate[key], value))));
    if (lostMetadata) { notice(path, "Nicht übernommen: Zusätzliche Metadaten vorhandener Auswahlen könnten verloren gehen."); return unchanged(current); }
    if (!arrayFits(next, arraySchema, path)) return unchanged(current);
    return { value: next, facts: desired.map(option => option.fact!) };
  }

  function walk(raw: SafetySchema, current: unknown, context: Context, path: string, root: SafetySchema, depth = 0): Mapped {
    if (depth > 24) { notice(path, "Die Schema-Verschachtelung ist zu tief."); return unchanged(current); }
    const inspected = inspectSafetySchema(raw, root);
    if (inspected.reason) { notice(path, inspected.reason); return unchanged(current); }
    const schema = inspected.schema;
    if ("const" in schema) { notice(path, "Der Wert ist im Amazon-Schema konstant und wurde erhalten."); return unchanged(current); }
    if (inspected.type === "array") {
      if (!safetyRecord(schema.items)) { notice(path, "Die Amazon-Listenelemente sind nicht eindeutig beschrieben."); return unchanged(current); }
      const choice = choiceSchema(schema.items, root);
      if (choice) return applyChoices(current, choice, context, path, root, schema);
      if (current != null && !Array.isArray(current)) { notice(path, "Die vorhandene Struktur wurde erhalten."); return unchanged(current); }
      const old = Array.isArray(current) ? current : [];
      if (old.length > 1) { notice(path, "Mehrere Datensätze sind nicht eindeutig zuordenbar; vorhandene Angaben wurden erhalten."); return unchanged(current); }
      const mapped = walk(schema.items, old[0], context, `${path}[0]`, root, depth + 1);
      if (!mapped.facts.length) return unchanged(current);
      const next = [mapped.value];
      return arrayFits(next, schema, path) ? { value: next, facts: mapped.facts } : unchanged(current);
    }
    if (inspected.type === "object" && safetyRecord(schema.properties)) {
      if (current != null && !safetyRecord(current)) { notice(path, "Die vorhandene Struktur wurde erhalten."); return unchanged(current); }
      const next = { ...metadataSeed(schema, root, marketplaceId), ...(safetyRecord(current) ? current : {}) };
      if (context === "warning" && typeof next.language_tag === "string" && !/^de(?:[_-]|$)/i.test(next.language_tag)) {
        notice(path, "Deutscher Warntext wurde wegen der vorhandenen abweichenden Sprachangabe nicht übernommen."); return unchanged(current);
      }
      const facts: string[] = [];
      for (const [name, child] of Object.entries(schema.properties)) {
        if (!safetyRecord(child) || /^(marketplace_id|language_tag)$/.test(name) || BLOCKED_NAMES.test(name)) continue;
        const mapped = walk(child, next[name], contextFor(name, name === "value" ? context : "codes"), `${path}.${name}`, root, depth + 1);
        if (mapped.facts.length) { next[name] = mapped.value; facts.push(...mapped.facts); }
      }
      if (!facts.length || !requiredSatisfied(next, schema, path)) return unchanged(current);
      return { value: next, facts };
    }
    const choice = choiceSchema(schema, root);
    if (choice) return applyChoices(current, choice, context, path, root);
    if (inspected.type === "string" && context === "warning") {
      if (!allowGermanText) { notice(path, "Der deutsche Warntext wurde nicht in ein fremdsprachiges Zielfeld übernommen."); return unchanged(current); }
      const warning = FRAGRANCE_SAFETY_PRESET.safetyWarning;
      if (current != null && current !== warning && (typeof current !== "string" || current.trim() !== "")) {
        notice(path, "Der vorhandene Warntext wurde erhalten. Prüfe und ergänze ihn bei Bedarf anhand des aktuellen Sicherheitsdatenblatts."); return unchanged(current);
      }
      if (typeof schema.maxLength === "number" && warning.length > schema.maxLength || typeof schema.minLength === "number" && warning.length < schema.minLength || typeof schema.pattern === "string") {
        notice(path, "Der vollständige deutsche Warntext kann wegen der Textvorgaben nicht sicher übernommen werden."); return unchanged(current);
      }
      return { value: warning, facts: ["safety_warning"] };
    }
    return unchanged(current);
  }

  for (const field of fields) {
    if (BLOCKED_NAMES.test(field.name)) continue;
    if (!field.editable) { notice(field.label, field.reason || "Dieses Feld ist schreibgeschützt."); continue; }
    const current = Object.prototype.hasOwnProperty.call(attributes, field.name) ? attributes[field.name] : field.currentValue;
    const mapped = walk(field.schema, current, contextFor(field.name), field.label, field.schema);
    if (mapped.facts.length) {
      mapped.facts.forEach(fact => covered.add(fact));
      if (!equal(current, mapped.value)) { result[field.name] = mapped.value; appliedFields.push(field.name); }
    }
  }
  const missing = FACTS.filter(fact => !covered.has(fact)).map(fact => fact === "warning" ? "Signalwort Achtung" : fact);
  if (missing.length) notices.add(`Nicht automatisch übernommen (keine eindeutig nutzbare Amazon-Auswahl): ${missing.join(", ")}.`);
  notices.add("GPSR, UFI, Herstellerkontakte und Transportangaben bleiben ungeändert: Dieser reine GHS-Helfer ordnet diese Angaben nicht zu; dafür ist das produktbezogene Sicherheitsprofil vorgesehen.");
  return { attributes: result, appliedFields, notices: [...notices] };
}
